import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './DepositModal.css';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  insufficientFunds?: boolean;
}

const DepositModal: React.FC<DepositModalProps> = ({ isOpen, onClose, insufficientFunds = false }) => {
  const { user, refreshBalances } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Environment detection from env variable
  const isTestnet = process.env.REACT_APP_IS_TESTNET === 'true';
  
  const handleTransferCrypto = () => {
    if (isTestnet) {
      // Testnet: Copy wallet address and redirect to USDC faucet
      if (user?.walletAddress) {
        navigator.clipboard.writeText(user.walletAddress);
        console.log('Wallet address copied to clipboard:', user.walletAddress);
        
        // Open Polygon faucet in new tab
        window.open('https://faucet.circle.com/', '_blank');
        
        // TODO: Show toast notification: "Wallet address copied! Paste it in the faucet."
        alert('Wallet address copied to clipboard! Paste it in the USDC faucet page that just opened. Make sure to select "Base Sepolia" network.');
      }
    } else {
      // Production: Show wallet address for direct transfers
      console.log('Production: Show transfer instructions');
    }
  };

  const handleCardDeposit = () => {
    setError(null);
    
    try {
      setIsLoading(true);
      
      // OnRamper widget URL-based integration
      const onramperConfig = {
        // API Key will be needed once obtained from OnRamper sales
        // apiKey: process.env.REACT_APP_ONRAMPER_API_KEY,
        
        // Required parameters
        defaultFiat: 'USD',
        defaultCrypto: 'USDC',
        defaultNetwork: 'base',
        defaultAmount: '100',
        
        // Force USDC on Base network
        onlyCryptos: 'usdc_base',
        networkWallets: `base:${user?.walletAddress}`,
        
        // UI customization
        theme: 'light',
        primaryColor: '#6366f1',
        
        // Testing mode
        isTestMode: isTestnet ? 'true' : 'false',
        
        // Remove skipTransactionScreen to allow proper flow
        // skipTransactionScreen: 'true',
      };
      
      // Build OnRamper widget URL
      const params = new URLSearchParams();
      Object.entries(onramperConfig).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const onramperUrl = `https://buy.onramper.com/?${params.toString()}`;
      
      console.log('Opening OnRamper widget:', onramperUrl);
      
      // Open OnRamper widget in new window
      const onramperWindow = window.open(
        onramperUrl,
        'onramper-widget',
        'width=500,height=700,scrollbars=yes,resizable=yes'
      );
      
      // Listen for window close to refresh balances
      const checkClosed = setInterval(() => {
        if (onramperWindow?.closed) {
          clearInterval(checkClosed);
          setIsLoading(false);
          
          // Refresh balances after widget closes
          if (refreshBalances) {
            console.log('OnRamper widget closed, refreshing balances...');
            refreshBalances();
          }
        }
      }, 1000);
      
      // Set loading to false immediately since widget opens in new window
      setTimeout(() => setIsLoading(false), 1000);
      
    } catch (error) {
      console.error('OnRamper widget failed to open:', error);
      setError('Failed to open deposit widget. Please try again.');
      setIsLoading(false);
    }
  };

  const handlePayPalDeposit = () => {
    if (isTestnet) {
      // Testnet: Simulate PayPal deposit
      console.log('Testnet: Simulating PayPal deposit');
      // TODO: Implement simulated PayPal deposit flow
    } else {
      // Production: Use Magic + PayPal integration
      console.log('Production: Opening PayPal on-ramp');
      // TODO: Implement PayPal on-ramp via Magic
    }
  };

  if (!isOpen) return null;

  return (
    <div className="deposit-modal-overlay" onClick={onClose}>
      <div className="deposit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="deposit-modal-header">
          <h2 className="deposit-modal-title">
            {insufficientFunds ? "You have insufficient funds. Deposit now" : "Deposit"}
          </h2>
          <button className="deposit-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="deposit-balance">
          <span className="balance-label">
            {isTestnet ? 'Testnet' : 'The Parlay Company'} Balance: {user?.cashBalance || '$0.00'}
          </span>
        </div>

        {error && (
          <div className="deposit-error" style={{ color: '#ff4444', padding: '10px', marginBottom: '10px', backgroundColor: '#fff1f1', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        <div className="deposit-methods">
          <div className="deposit-method" onClick={handleTransferCrypto}>
            <div className="method-icon lightning-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2"/>
              </svg>
            </div>
            <div className="method-details">
              <h3 className="method-title">Transfer Crypto</h3>
              <p className="method-description">No limit • Instant</p>
            </div>
            <div className="method-icons">
              {/* Crypto logos */}
              <div className="crypto-icons">
                <div className="crypto-icon btc">₿</div>
                <div className="crypto-icon eth">Ξ</div>
                <div className="crypto-icon usdc">USDC</div>
                <div className="crypto-icon">+</div>
              </div>
            </div>
          </div>

          <div className="deposit-method" onClick={isLoading ? undefined : handleCardDeposit} style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
            <div className="method-icon card-icon">
              {isLoading ? (
                <div style={{ width: '24px', height: '24px', border: '2px solid #f3f3f3', borderTop: '2px solid #333', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              )}
            </div>
            <div className="method-details">
              <h3 className="method-title">{isLoading ? 'Opening OnRamper...' : 'Deposit with Card'}</h3>
              <p className="method-description">{isTestnet ? 'Testnet • OnRamper Widget' : '25+ providers • 130+ payment methods'}</p>
            </div>
            <div className="method-icons">
              <div className="new-badge">NEW</div>
              <div className="payment-icons">
                <div className="payment-icon mastercard">MC</div>
                <div className="payment-icon visa">VISA</div>
              </div>
            </div>
          </div>

          <div className="deposit-method" onClick={handlePayPalDeposit}>
            <div className="method-icon paypal-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" fill="#253B80"/>
              </svg>
            </div>
            <div className="method-details">
              <h3 className="method-title">Deposit with PayPal</h3>
              <p className="method-description">$10,000 limit • 5 min</p>
            </div>
            <div className="method-icons">
              <div className="new-badge">NEW</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;